export const FORTRESS_TERRAIN_WIDTH = 144;
export const FORTRESS_TERRAIN_HEIGHT = 64;
export const FORTRESS_PLAYER_RADIUS = 2.25;
export const FORTRESS_MIN_ANGLE = 15;
export const FORTRESS_MAX_ANGLE = 165;
export const FORTRESS_MIN_POWER = 30;
export const FORTRESS_MAX_POWER = 100;
export const FORTRESS_POWER_POINTS_PER_SECOND = 65;
export const FORTRESS_MOVE_PER_TURN = 16;
export const FORTRESS_MOVE_STEP = 1;
export const FORTRESS_MAX_MOVE_HEIGHT_DELTA = 3;
export const FORTRESS_MAX_WIND_MPS = 6;
export const FORTRESS_WIND_ACCELERATION_SCALE = 1;
export const FORTRESS_GRAVITY = 18;
export const FORTRESS_PROJECTILE_STEP_SECONDS = 0.05;
export const FORTRESS_PROJECTILE_MAX_MILLISECONDS = 8_000;
export const FORTRESS_FLIGHT_TIME_SCALE = 2;

export const FORTRESS_MAP_IDS = [
  'rolling-hills',
  'central-highland',
  'crosswind-ridges',
  'twin-peaks',
  'moon-basin',
  'tilted-land',
] as const;
export type FortressMapId = typeof FORTRESS_MAP_IDS[number];

export interface FortressMapSpec {
  readonly id: FortressMapId;
  readonly label: string;
}

export const FORTRESS_MAPS: Readonly<Record<FortressMapId, FortressMapSpec>> = {
  'rolling-hills': { id: 'rolling-hills', label: '파도 구릉' },
  'central-highland': { id: 'central-highland', label: '중앙 고지' },
  'crosswind-ridges': { id: 'crosswind-ridges', label: '엇갈린 능선' },
  'twin-peaks': { id: 'twin-peaks', label: '쌍둥이 봉우리' },
  'moon-basin': { id: 'moon-basin', label: '달빛 분지' },
  'tilted-land': { id: 'tilted-land', label: '기울어진 대지' },
};

export const FORTRESS_WEAPON_IDS = ['coconut', 'precision-missile'] as const;
export type FortressWeaponId = typeof FORTRESS_WEAPON_IDS[number];

export interface FortressWeaponSpec {
  readonly id: FortressWeaponId;
  readonly label: string;
  readonly shortLabel: string;
  readonly maximumDamage: number;
  readonly damageRadius: number;
  readonly craterRadius: number;
  readonly projectileHitRadius: number;
}

export const FORTRESS_WEAPONS: Readonly<Record<FortressWeaponId, FortressWeaponSpec>> = {
  coconut: {
    id: 'coconut',
    label: '기본 코코넛',
    shortLabel: '코코넛',
    maximumDamage: 52,
    damageRadius: 13,
    craterRadius: 7,
    projectileHitRadius: 0.8,
  },
  'precision-missile': {
    id: 'precision-missile',
    label: '정밀 미사일',
    shortLabel: '미사일',
    maximumDamage: 72,
    damageRadius: 6.5,
    craterRadius: 4,
    projectileHitRadius: 0.35,
  },
};

export const FORTRESS_CRATER_RADIUS = FORTRESS_WEAPONS.coconut.craterRadius;
export const FORTRESS_DAMAGE_RADIUS = FORTRESS_WEAPONS.coconut.damageRadius;
export const FORTRESS_MAX_DAMAGE = FORTRESS_WEAPONS.coconut.maximumDamage;

export type FortressMode = 'countdown' | 'turn' | 'flight' | 'impact' | 'complete';

export interface FortressPoint {
  readonly x: number;
  readonly y: number;
}

export interface FortressPlayerPosition extends FortressPoint {
  readonly id: string;
  readonly alive: boolean;
}

export interface FortressProjectile {
  readonly ownerId: string;
  readonly weapon: FortressWeaponId;
  x: number;
  y: number;
  vx: number;
  vy: number;
  ageMilliseconds: number;
}

export interface FortressProjectileStep {
  readonly projectile: FortressProjectile;
  readonly impact: FortressPoint | null;
  readonly hitPlayerId: string | null;
  readonly missed: boolean;
}

export interface FortressDamageView {
  readonly playerId: string;
  readonly amount: number;
}

export interface FortressTerrainChange {
  readonly index: number;
  readonly height: number;
}

export interface FortressExplosionView extends FortressPoint {
  readonly weapon: FortressWeaponId;
  readonly radius: number;
  readonly directHitPlayerId: string | null;
  readonly damage: readonly FortressDamageView[];
}

export interface FortressPlayerView extends FortressPlayerPosition {
  readonly nickname: string;
  readonly colorIndex: number;
  readonly health: number;
}

export interface FortressView {
  readonly mode: FortressMode;
  readonly matchId: string;
  readonly mapId: FortressMapId;
  readonly phaseId: number;
  readonly turnId: number;
  readonly turnNumber: number;
  readonly turnPlayerId: string | null;
  readonly deadlineAt: number;
  readonly matchDeadlineAt: number;
  readonly windMps: number;
  readonly movementRemaining: number;
  readonly suddenDeath: boolean;
  readonly terrainVersion: number;
  readonly terrain: readonly number[] | null;
  readonly terrainPatch: readonly FortressTerrainChange[];
  readonly resetTerrain: boolean;
  readonly projectile: Readonly<FortressProjectile> | null;
  readonly explosion: FortressExplosionView | null;
  readonly winnerId: string | null;
  readonly players: readonly FortressPlayerView[];
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

export function isFortressWeaponId(value: unknown): value is FortressWeaponId {
  return value === 'coconut' || value === 'precision-missile';
}

export function isFortressMapId(value: unknown): value is FortressMapId {
  return typeof value === 'string' && FORTRESS_MAP_IDS.includes(value as FortressMapId);
}

export function fortressMapId(seed: number): FortressMapId {
  return FORTRESS_MAP_IDS[(seed >>> 0) % FORTRESS_MAP_IDS.length];
}

export function fortressMapSpec(mapId: FortressMapId): FortressMapSpec {
  return FORTRESS_MAPS[mapId];
}

export function fortressWeaponSpec(weapon: FortressWeaponId): FortressWeaponSpec {
  return FORTRESS_WEAPONS[weapon];
}

function seededRandom(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value = (value + 0x6d2b79f5) >>> 0;
    let mixed = value;
    mixed = Math.imul(mixed ^ (mixed >>> 15), mixed | 1);
    mixed ^= mixed + Math.imul(mixed ^ (mixed >>> 7), mixed | 61);
    return ((mixed ^ (mixed >>> 14)) >>> 0) / 4_294_967_296;
  };
}

export function fortressTerrainWidth(playerCount: number): number {
  return FORTRESS_TERRAIN_WIDTH + (clamp(Math.round(playerCount), 2, 4) - 2) * 24;
}

export function createFortressTerrain(
  seed: number,
  width = FORTRESS_TERRAIN_WIDTH,
  height = FORTRESS_TERRAIN_HEIGHT,
): number[] {
  const random = seededRandom(seed);
  const mapId = fortressMapId(seed);
  const mound = (progress: number, center: number, spread: number): number => (
    Math.exp(-(((progress - center) / spread) ** 2))
  );
  let terrain = Array.from({ length: width }, (_, x) => {
    const progress = x / Math.max(1, width - 1);
    const noise = (random() - 0.5) * 3.2;
    if (mapId === 'rolling-hills') {
      return 43 + Math.sin(progress * Math.PI * 2.2) * 5
        + Math.sin(progress * Math.PI * 5.6 + 0.7) * 2.2 + noise;
    }
    if (mapId === 'central-highland') {
      return 48 - Math.sin(progress * Math.PI) * 11
        + Math.sin(progress * Math.PI * 4.5 + 1.3) * 2.7 + noise;
    }
    if (mapId === 'crosswind-ridges') {
      return 42 + Math.sin(progress * Math.PI * 3.4 + 0.4) * 6
        + Math.cos(progress * Math.PI * 7.2) * 1.7 + noise;
    }
    if (mapId === 'twin-peaks') {
      return 51 - mound(progress, 0.27, 0.12) * 14
        - mound(progress, 0.73, 0.12) * 14
        + mound(progress, 0.5, 0.09) * 4
        + Math.sin(progress * Math.PI * 5) * 1.2 + noise * 0.55;
    }
    if (mapId === 'moon-basin') {
      return 34 + mound(progress, 0.5, 0.22) * 19
        - mound(progress, 0.15, 0.13) * 3
        - mound(progress, 0.85, 0.13) * 3
        + Math.sin(progress * Math.PI * 4 + 0.3) + noise * 0.5;
    }
    return 34 + progress * 18
      + Math.sin(progress * Math.PI * 4 + 0.5) * 2.8 + noise * 0.65;
  });
  for (let pass = 0; pass < 4; pass += 1) {
    terrain = terrain.map((value, index, values) => (
      (values[Math.max(0, index - 1)] + value * 2 + values[Math.min(values.length - 1, index + 1)]) / 4
    ));
  }
  return terrain.map((value) => Math.round(clamp(value, height * 0.43, height - 8) * 10) / 10);
}

export function fortressSpawnXs(
  playerCount: number,
  width = fortressTerrainWidth(playerCount),
  seed?: number,
): number[] {
  const ratios = playerCount === 2
    ? [0.13, 0.87]
    : playerCount === 3
      ? [0.1, 0.5, 0.9]
      : [0.08, 0.36, 0.64, 0.92];
  const slots = ratios.slice(0, playerCount);
  if (seed === undefined) return slots.map((ratio) => Math.round((width - 1) * ratio));
  const random = seededRandom(seed ^ 0x85ebca6b);
  const jitter = playerCount === 2 ? 0.055 : playerCount === 3 ? 0.04 : 0.025;
  const minimumX = Math.ceil(FORTRESS_PLAYER_RADIUS + 2);
  const maximumX = Math.floor(width - 1 - FORTRESS_PLAYER_RADIUS - 2);
  const shuffled = slots.map((ratio) => Math.round(clamp(
    (width - 1) * (ratio + (random() - 0.5) * jitter * 2),
    minimumX,
    maximumX,
  )));
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled;
}

export function fortressTurnOrder(playerIds: readonly string[], seed: number): string[] {
  const result = [...playerIds];
  const random = seededRandom(seed ^ 0x9e3779b9);
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
  }
  return result;
}

function rawFortressWind(seed: number, turnNumber: number): number {
  const random = seededRandom(seed ^ Math.imul(turnNumber + 17, 0x45d9f3b));
  const raw = (random() * FORTRESS_MAX_WIND_MPS * 2) - FORTRESS_MAX_WIND_MPS;
  return Math.round(raw * 10) / 10;
}

export function fortressWind(seed: number, turnNumber: number): number {
  if (turnNumber <= 0) return rawFortressWind(seed, turnNumber);
  let previous = rawFortressWind(seed, 0);
  for (let turn = 1; turn <= turnNumber; turn += 1) {
    let wind = rawFortressWind(seed, turn);
    if (wind === previous) {
      wind = Math.round((wind >= FORTRESS_MAX_WIND_MPS - 0.4 ? wind - 0.5 : wind + 0.5) * 10) / 10;
    }
    previous = wind;
  }
  return previous;
}

export function fortressPlayerMoveDestination(
  player: FortressPlayerPosition,
  direction: -1 | 1,
  terrain: readonly number[],
  players: readonly FortressPlayerPosition[],
): FortressPoint | null {
  const targetX = player.x + direction * FORTRESS_MOVE_STEP;
  if (targetX < FORTRESS_PLAYER_RADIUS
    || targetX > terrain.length - 1 - FORTRESS_PLAYER_RADIUS) return null;
  const sourceHeight = terrain[Math.round(clamp(player.x, 0, terrain.length - 1))];
  const targetHeight = terrain[Math.round(targetX)];
  if (!Number.isFinite(sourceHeight) || !Number.isFinite(targetHeight)
    || Math.abs(targetHeight - sourceHeight) > FORTRESS_MAX_MOVE_HEIGHT_DELTA) return null;
  const targetY = targetHeight - FORTRESS_PLAYER_RADIUS;
  const overlapsPlayer = players.some((candidate) => candidate.id !== player.id
    && candidate.alive
    && Math.hypot(targetX - candidate.x, targetY - candidate.y) < FORTRESS_PLAYER_RADIUS * 2);
  return overlapsPlayer ? null : { x: targetX, y: targetY };
}

export function fortressPowerAtElapsed(elapsedMilliseconds: number): number {
  const range = FORTRESS_MAX_POWER - FORTRESS_MIN_POWER;
  const traveled = Math.max(0, elapsedMilliseconds) / 1_000 * FORTRESS_POWER_POINTS_PER_SECOND;
  const phase = traveled % (range * 2);
  return Math.round(FORTRESS_MIN_POWER + (phase <= range ? phase : range * 2 - phase));
}

export function createFortressProjectile(
  owner: FortressPoint & { readonly id: string },
  angle: number,
  power: number,
  weapon: FortressWeaponId = 'coconut',
): FortressProjectile {
  const radians = clamp(angle, FORTRESS_MIN_ANGLE, FORTRESS_MAX_ANGLE) * Math.PI / 180;
  const speed = 14 + clamp(power, FORTRESS_MIN_POWER, FORTRESS_MAX_POWER) * 0.38;
  const directionX = Math.cos(radians);
  const directionY = -Math.sin(radians);
  return {
    ownerId: owner.id,
    weapon,
    x: owner.x + directionX * 2.7,
    y: owner.y + directionY * 2.7,
    vx: directionX * speed,
    vy: directionY * speed,
    ageMilliseconds: 0,
  };
}

function terrainHeightAt(terrain: readonly number[], x: number): number {
  return terrain[Math.round(clamp(x, 0, terrain.length - 1))];
}

function projectileHitPlayerId(
  projectile: FortressProjectile,
  players: readonly FortressPlayerPosition[],
): string | null {
  if (projectile.ageMilliseconds < 180) return null;
  const hitRadius = fortressWeaponSpec(projectile.weapon).projectileHitRadius;
  return players.find((player) => player.alive
    && Math.hypot(projectile.x - player.x, projectile.y - player.y) <= FORTRESS_PLAYER_RADIUS + hitRadius)?.id ?? null;
}

export function stepFortressProjectile(
  source: Readonly<FortressProjectile>,
  windMps: number,
  terrain: readonly number[],
  players: readonly FortressPlayerPosition[],
  deltaSeconds = FORTRESS_PROJECTILE_STEP_SECONDS,
): FortressProjectileStep {
  const projectile: FortressProjectile = { ...source };
  const speed = Math.hypot(projectile.vx, projectile.vy);
  const substeps = Math.max(1, Math.ceil(speed * deltaSeconds / 0.55));
  const substepSeconds = deltaSeconds / substeps;
  for (let index = 0; index < substeps; index += 1) {
    projectile.vx += windMps * FORTRESS_WIND_ACCELERATION_SCALE * substepSeconds;
    projectile.vy += FORTRESS_GRAVITY * substepSeconds;
    projectile.x += projectile.vx * substepSeconds;
    projectile.y += projectile.vy * substepSeconds;
    projectile.ageMilliseconds += substepSeconds * 1_000;

    if (projectile.x < -2 || projectile.x > terrain.length + 1
      || projectile.y > FORTRESS_TERRAIN_HEIGHT + 8
      || projectile.ageMilliseconds >= FORTRESS_PROJECTILE_MAX_MILLISECONDS) {
      return { projectile, impact: null, hitPlayerId: null, missed: true };
    }
    const hitPlayerId = projectileHitPlayerId(projectile, players);
    if (projectile.y >= -2 && projectile.x >= 0 && projectile.x < terrain.length
      && (projectile.y >= terrainHeightAt(terrain, projectile.x)
        || hitPlayerId !== null)) {
      return {
        projectile,
        impact: { x: clamp(projectile.x, 0, terrain.length - 1), y: projectile.y },
        hitPlayerId,
        missed: false,
      };
    }
  }
  return { projectile, impact: null, hitPlayerId: null, missed: false };
}

export function deformFortressTerrain(
  terrain: readonly number[],
  impact: FortressPoint,
  radius = FORTRESS_CRATER_RADIUS,
  maximumHeight = FORTRESS_TERRAIN_HEIGHT,
): number[] {
  return terrain.map((height, x) => {
    const distanceX = Math.abs(x - impact.x);
    if (distanceX > radius) return height;
    const depth = Math.sqrt(Math.max(0, radius ** 2 - distanceX ** 2));
    return Math.round(Math.max(height, Math.min(maximumHeight - 2, impact.y + depth)) * 10) / 10;
  });
}

export function fortressTerrainPatch(
  previous: readonly number[],
  next: readonly number[],
): FortressTerrainChange[] {
  return next.flatMap((height, index) => previous[index] === height ? [] : [{ index, height }]);
}

export function applyFortressTerrainPatch(
  terrain: readonly number[],
  patch: readonly FortressTerrainChange[],
): number[] {
  const result = [...terrain];
  for (const change of patch) {
    if (Number.isSafeInteger(change.index) && change.index >= 0 && change.index < result.length
      && Number.isFinite(change.height)) result[change.index] = change.height;
  }
  return result;
}

export function fortressExplosionDamage(
  player: FortressPoint,
  impact: FortressPoint,
  multiplier = 1,
  weapon: FortressWeaponId = 'coconut',
  directHit = false,
): number {
  const spec = fortressWeaponSpec(weapon);
  if (directHit) return Math.round(spec.maximumDamage * multiplier);
  const distance = Math.hypot(player.x - impact.x, player.y - impact.y);
  if (distance >= spec.damageRadius) return 0;
  return Math.max(1, Math.round(spec.maximumDamage * (1 - distance / spec.damageRadius) * multiplier));
}

export function predictFortressTrajectory(
  owner: FortressPoint & { readonly id: string },
  angle: number,
  power: number,
  windMps: number,
  terrain: readonly number[],
  players: readonly FortressPlayerPosition[],
  weapon: FortressWeaponId = 'coconut',
): FortressPoint[] {
  let projectile = createFortressProjectile(owner, angle, power, weapon);
  const points: FortressPoint[] = [{ x: projectile.x, y: projectile.y }];
  for (let step = 0; step < 160; step += 1) {
    const result = stepFortressProjectile(projectile, windMps, terrain, players);
    projectile = result.projectile;
    if (step % 3 === 0 || result.impact || result.missed) points.push({ x: projectile.x, y: projectile.y });
    if (result.impact || result.missed) break;
  }
  return points;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isPlayerView(value: unknown): value is FortressPlayerView {
  return isRecord(value)
    && typeof value.id === 'string'
    && typeof value.nickname === 'string'
    && Number.isSafeInteger(value.colorIndex)
    && Number.isSafeInteger(value.health)
    && isFiniteNumber(value.x)
    && isFiniteNumber(value.y)
    && typeof value.alive === 'boolean';
}

function isProjectile(value: unknown): value is FortressProjectile {
  return isRecord(value)
    && typeof value.ownerId === 'string'
    && isFortressWeaponId(value.weapon)
    && isFiniteNumber(value.x)
    && isFiniteNumber(value.y)
    && isFiniteNumber(value.vx)
    && isFiniteNumber(value.vy)
    && isFiniteNumber(value.ageMilliseconds);
}

function isExplosion(value: unknown): value is FortressExplosionView {
  return isRecord(value)
    && isFortressWeaponId(value.weapon)
    && isFiniteNumber(value.x)
    && isFiniteNumber(value.y)
    && isFiniteNumber(value.radius)
    && (value.directHitPlayerId === null || typeof value.directHitPlayerId === 'string')
    && Array.isArray(value.damage)
    && value.damage.every((entry) => isRecord(entry)
      && typeof entry.playerId === 'string'
      && Number.isSafeInteger(entry.amount));
}

export function isFortressView(value: unknown): value is FortressView {
  return isRecord(value)
    && (value.mode === 'countdown' || value.mode === 'turn' || value.mode === 'flight'
      || value.mode === 'impact' || value.mode === 'complete')
    && typeof value.matchId === 'string'
    && isFortressMapId(value.mapId)
    && Number.isSafeInteger(value.phaseId)
    && Number.isSafeInteger(value.turnId)
    && Number.isSafeInteger(value.turnNumber)
    && (value.turnPlayerId === null || typeof value.turnPlayerId === 'string')
    && isFiniteNumber(value.deadlineAt)
    && isFiniteNumber(value.matchDeadlineAt)
    && isFiniteNumber(value.windMps)
    && typeof value.movementRemaining === 'number'
    && Number.isSafeInteger(value.movementRemaining)
    && value.movementRemaining >= 0
    && value.movementRemaining <= FORTRESS_MOVE_PER_TURN
    && typeof value.suddenDeath === 'boolean'
    && Number.isSafeInteger(value.terrainVersion)
    && (value.terrain === null || (Array.isArray(value.terrain)
      // Also accept the previous map size during a rolling client/server update.
      && [120, fortressTerrainWidth(2), fortressTerrainWidth(3), fortressTerrainWidth(4)].includes(value.terrain.length)
      && value.terrain.every(isFiniteNumber)))
    && Array.isArray(value.terrainPatch)
    && value.terrainPatch.every((change) => isRecord(change)
      && Number.isSafeInteger(change.index)
      && isFiniteNumber(change.height))
    && typeof value.resetTerrain === 'boolean'
    && (value.projectile === null || isProjectile(value.projectile))
    && (value.explosion === null || isExplosion(value.explosion))
    && (value.winnerId === null || typeof value.winnerId === 'string')
    && Array.isArray(value.players)
    && value.players.every(isPlayerView);
}
